<script setup lang="ts">
import { ApiError, NetworkError } from "../lib/api"
import { lookupOrderThread, startOrderThread, useMessaging } from "../composables/useMessaging"
import { t } from "../lib/i18n"

// An order's conversation with its customer, beside the order (the POS's order view), or a first
// message to start it. The order's details must be in Hub first (the POS's /<shop>/hub-order).
// Rich content: the message-body slot, passed on to HubThreadView.
const props = withDefaults(defineProps<{ organisationId: string; orderId: string; theme?: "app" | "pos" }>(), { theme: "app" })
provide("hubTheme", props.theme)

const hub = useMessaging()
/** undefined while looking, null when nobody has written yet. */
const threadId = ref<string | null | undefined>(undefined)
const thread = computed(() => hub.threads.find((th) => th.id === threadId.value) ?? null)
const draft = ref("")
const busy = ref(false)
const error = ref("")

async function look() {
  threadId.value = undefined
  error.value = ""
  try {
    threadId.value = await lookupOrderThread(props.organisationId, props.orderId)
  } catch (err) {
    threadId.value = null
    error.value = err instanceof NetworkError ? t("Messages can't be reached right now.") : err instanceof Error ? err.message : ""
  }
}

onMounted(look)
watch(() => [props.organisationId, props.orderId], look)

async function start() {
  if (!draft.value.trim()) return
  busy.value = true
  error.value = ""
  try {
    threadId.value = await startOrderThread(props.organisationId, props.orderId, draft.value)
    draft.value = ""
  } catch (err) {
    error.value =
      err instanceof NetworkError
        ? t("You're offline. The first message needs a connection.")
        : err instanceof ApiError
          ? err.message
          : t("Something went wrong.")
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="order-conversation" :class="theme === 'pos' ? 'hub-ui hub-theme-pos' : ''">
    <HubThreadView v-if="thread" :thread="thread" hide-back order-view>
      <template v-if="$slots['message-body']" #message-body="slot"><slot name="message-body" v-bind="slot" /></template>
    </HubThreadView>
    <div v-else-if="threadId" class="order-start"><p class="hint">{{ t("Loading…") }}</p></div>
    <form v-else-if="threadId === null" class="order-start" @submit.prevent="start">
      <p class="hint">
        {{ t("No messages about this order yet. The customer gets an email with a link to the order page, where they can read and reply.") }}
      </p>
      <label class="hub-sr-only" for="first-message">{{ t("Write to the customer") }}</label>
      <textarea id="first-message" v-model="draft" class="hub-field" rows="4" :placeholder="t('Write to the customer')" />
      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <button class="hub-btn" type="submit" :disabled="busy || !draft.trim()"><HubIcon name="send" :size="18" />{{ t("Send") }}</button>
    </form>
    <div v-else class="order-start"><p class="hint">{{ t("Loading…") }}</p></div>
  </div>
</template>

<style scoped>
.order-conversation {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.order-start {
  padding: 16px 15px;
  display: grid;
  gap: 12px;
  align-content: start;
  max-width: 640px;
}

.hint {
  margin: 0;
  color: var(--hub-muted);
}

.error {
  margin: 0;
  color: var(--hub-danger);
}

textarea {
  resize: vertical;
}

.hub-btn {
  justify-self: start;
}
</style>
